import React from "react";
import { InlineMath } from "react-katex";
import StatisticsBarChartDiagram from "../../../../components/diagrams/StatisticsBarChartDiagram";
import {
  makeClosedTask,
  normalizeMay2026Variant,
  pick,
  renderClosedTask,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const CHARTS = [{A:[1,6,3,3,6,1],B:[1,3,6,6,3,1]},{A:[1,2,4,6,4,3],B:[2,3,4,5,4,2]},{A:[2,5,4,3,4,2],B:[1,4,5,5,4,1]},{A:[3,4,5,4,3,1],B:[1,3,4,5,4,3]},{A:[1,5,5,4,3,2],B:[2,3,4,5,5,1]},{A:[2,4,6,4,2,2],B:[1,5,5,5,3,1]},{A:[1,4,6,5,3,1],B:[2,3,5,6,3,1]},{A:[2,5,3,4,5,1],B:[1,4,5,4,4,2]}];
const RELATIONS = {
  equal: { phrase: "jest równa", compare: (left, right) => Math.abs(left - right) < 1e-9 },
  greater: { phrase: "jest większa od", compare: (left, right) => left > right },
  less: { phrase: "jest mniejsza od", compare: (left, right) => left < right },
};
const RELATION_KEYS = Object.keys(RELATIONS);

function ClassChart({ className, counts, color }) {
  return (
    <figure style={{ margin: 0, minWidth: 0 }}>
      <figcaption style={{ textAlign: "center", fontWeight: 700, marginBottom: 4 }}>
        klasa <InlineMath math={`\\mathrm{IV\\,${className}}`} />
      </figcaption>
      <div style={{ paddingInline: "clamp(6px, 1.5vw, 18px)" }}>
        <StatisticsBarChartDiagram
          categories={[1, 2, 3, 4, 5, 6]}
          counts={counts}
          xLabel="ocena"
          yLabel="liczba uczniów"
          barColor={color}
          yMax={7}
          yLabelStep={1}
          maxBarHeight={150}
          minHeight={218}
        />
      </div>
    </figure>
  );
}

function Charts({ rows }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "clamp(12px, 3vw, 36px)", margin: "18px auto", maxWidth: 760 }}>
      <ClassChart className="A" counts={rows.A} color="#ff9900" />
      <ClassChart className="B" counts={rows.B} color="#8b5cf6" />
    </div>
  );
}

function stats(row) {
  const values = row.flatMap((count, index) => Array(count).fill(index + 1));
  const sum = values.reduce((total, value) => total + value, 0);
  const mean = sum / values.length;
  const middle = values.length / 2;
  const median = values.length % 2 ? values[Math.floor(middle)] : (values[middle - 1] + values[middle]) / 2;
  const middleValues = values.length % 2
    ? [values[Math.floor(middle)]]
    : [values[middle - 1], values[middle]];
  return { count: values.length, mean, median, middleValues, sum };
}

const decimalLatex = (value) => String(Number(value.toFixed(4))).replace(".", "{,}");

function countLatex(row) {
  return row.join("+");
}

function weightedSumLatex(row) {
  return row.map((count, index) => `${index + 1}\\cdot${count}`).join("+");
}

function medianLatex(label, result) {
  if (result.count % 2 === 1) {
    const position = (result.count + 1) / 2;
    return `m_${label}=x_{${position}}=${decimalLatex(result.median)}`;
  }
  const leftPosition = result.count / 2;
  const rightPosition = leftPosition + 1;
  const [leftValue, rightValue] = result.middleValues;
  return `m_${label}=\\frac{x_{${leftPosition}}+x_{${rightPosition}}}{2}=\\frac{${leftValue}+${rightValue}}{2}=${decimalLatex(result.median)}`;
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const rows = variant === "original" ? CHARTS[0] : pick(CHARTS);
  const a = stats(rows.A);
  const b = stats(rows.B);
  const relations = variant === "original"
    ? { mean: "equal", median: "equal" }
    : { mean: pick(RELATION_KEYS), median: pick(RELATION_KEYS) };
  return makeClosedTask({
    type: "MaturaTwoClassStatsTruthClosedTask",
    taskNumber: "31",
    variant,
    tags: ["statystyka"],
    data: {
      rows,
      relations,
      answerMode: "truth",
      statementTruth: [
        RELATIONS[relations.mean].compare(a.mean, b.mean),
        RELATIONS[relations.median].compare(a.median, b.median),
      ],
      instruction: "Oceń prawdziwość poniższych stwierdzeń. Wybierz P, jeśli stwierdzenie jest prawdziwe, albo F – jeśli jest fałszywe.",
      explanationSteps: [
        {
          title: "Z wykresów odczytujemy liczebności oraz obliczamy sumę wszystkich ocen w każdej klasie.",
          latex: `\\begin{aligned}
n_A&=${countLatex(rows.A)}=${a.count},\\\\
S_A&=${weightedSumLatex(rows.A)}=${a.sum},\\\\
n_B&=${countLatex(rows.B)}=${b.count},\\\\
S_B&=${weightedSumLatex(rows.B)}=${b.sum}.
\\end{aligned}`,
        },
        {
          title: "Sumę ocen dzielimy przez liczbę uczniów i otrzymujemy średnie arytmetyczne.",
          latex: `\\begin{aligned}
\\bar x_A&=\\frac{S_A}{n_A}=\\frac{${a.sum}}{${a.count}}=${decimalLatex(a.mean)},\\\\
\\bar x_B&=\\frac{S_B}{n_B}=\\frac{${b.sum}}{${b.count}}=${decimalLatex(b.mean)}.
\\end{aligned}`,
        },
        {
          title: "Oceny są już uporządkowane na wykresach. Dla parzystej liczby uczniów mediana jest średnią dwóch środkowych wyników.",
          latex: `\\begin{aligned}
${medianLatex("A", a)},\\\\
${medianLatex("B", b)}.
\\end{aligned}`,
        },
      ],
    },
  });
}

export function renderTask({ data }) {
  if (!data?.rows?.A || !data?.rows?.B) return renderClosedTask({ data });
  const relations = data.relations || { mean: "equal", median: "equal" };
  const meanRelation = RELATIONS[relations.mean] || RELATIONS.equal;
  const medianRelation = RELATIONS[relations.median] || RELATIONS.equal;

  return (
    <div style={{ lineHeight: 1.5 }}>
      <p style={{ marginTop: 0 }}>
        Nauczyciel matematyki po każdym sprawdzianie porównuje wyniki uzyskane przez uczniów dwóch klas: klasy <InlineMath math="\mathrm{IV\,A}" /> oraz klasy <InlineMath math="\mathrm{IV\,B}" />.
        Na dwóch poniższych diagramach przedstawiono wyniki sprawdzianu ze statystyki, jakie uzyskali uczniowie tych klas.
        Na osiach poziomych podano oceny, które uzyskali uczniowie tych klas, a na osiach pionowych podano liczbę uczniów, którzy otrzymali daną ocenę.
      </p>
      <Charts rows={data.rows} />
      <p style={{ margin: "18px 0 12px" }}><strong>Oceń prawdziwość poniższych stwierdzeń. Wybierz <InlineMath math="P" />, jeśli stwierdzenie jest prawdziwe, albo <InlineMath math="F" /> – jeśli jest fałszywe.</strong></p>
      <div className="matura-truth-statement-list">
        <div className="matura-truth-statement">
          <span className="matura-truth-statement__number"><InlineMath math="1." /></span>
          <span>
            Średnia arytmetyczna ocen uzyskanych ze sprawdzianu ze statystyki przez uczniów klasy <InlineMath math="\mathrm{IV\,A}" /> {meanRelation.phrase} średniej arytmetycznej ocen uzyskanych z tego sprawdzianu przez uczniów klasy <InlineMath math="\mathrm{IV\,B}" />.
          </span>
        </div>
        <div className="matura-truth-statement">
          <span className="matura-truth-statement__number"><InlineMath math="2." /></span>
          <span>
            Mediana ocen uzyskanych ze sprawdzianu ze statystyki przez uczniów klasy <InlineMath math="\mathrm{IV\,A}" /> {medianRelation.phrase} mediany ocen uzyskanych z tego sprawdzianu przez uczniów klasy <InlineMath math="\mathrm{IV\,B}" />.
          </span>
        </div>
      </div>
    </div>
  );
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
